/**
 * CheckProgress - Step progress indicator for tech check
 * Nordic Sanctuary design: Clear visual feedback
 */

import { Check, Camera, Mic, Volume2, Wifi } from "lucide-react";

type CheckStatus = "pending" | "running" | "pass" | "warning" | "fail";

interface Step {
  id: string;
  label: string;
  status: CheckStatus;
}

interface CheckProgressProps {
  steps: Step[];
  currentStep: number;
}

const stepIcons = {
  camera: Camera,
  microphone: Mic,
  speaker: Volume2,
  network: Wifi,
};

export function CheckProgress({ steps, currentStep }: CheckProgressProps) {
  return (
    <div className="flex items-center justify-between">
      {steps.map((step, index) => {
        const Icon = stepIcons[step.id as keyof typeof stepIcons] || Camera;
        const isActive = index === currentStep;
        const isPast = index < currentStep;
        const isCompleted = step.status === "pass";
        const isFailed = step.status === "fail";
        const isWarning = step.status === "warning";

        return (
          <div key={step.id} className="flex items-center">
            {/* Step indicator */}
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300
                  ${
                    isCompleted
                      ? "bg-[var(--sage-500)] text-white shadow-lg shadow-[var(--sage-300)]/30"
                      : isWarning
                        ? "bg-[oklch(0.92_0.08_80)] text-[oklch(0.50_0.15_80)]"
                        : isFailed
                          ? "bg-[oklch(0.95_0.04_25)] text-[oklch(0.55_0.18_25)]"
                          : isActive
                            ? "bg-[var(--sage-100)] text-[var(--sage-600)] ring-2 ring-[var(--sage-300)] ring-offset-2"
                            : isPast
                              ? "bg-[var(--sage-100)] text-[var(--sage-500)]"
                              : "bg-[var(--stone-100)] text-[var(--stone-400)]"
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="w-6 h-6" strokeWidth={3} />
                ) : isActive && step.status === "running" ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
              <span
                className={`
                  text-xs mt-2 font-medium transition-colors
                  ${
                    isActive || isPast || isCompleted
                      ? "text-[var(--stone-700)]"
                      : "text-[var(--stone-400)]"
                  }
                `}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                className={`
                  h-0.5 w-8 sm:w-12 mx-1 sm:mx-2 rounded-full transition-colors duration-300
                  ${isPast ? "bg-[var(--sage-400)]" : "bg-[var(--stone-200)]"}
                `}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
